package com.schoolconnect.app.repository.parentnotes

import com.schoolconnect.app.datasource.network.KtorApiClient
import com.schoolconnect.app.datasource.network.request.ParentNoteCreateRequest
import com.schoolconnect.app.datasource.network.request.ParentNoteUpdateRequest
import com.schoolconnect.app.datasource.network.request.ParentNoteReplyCreateRequest
import com.schoolconnect.app.datasource.network.response.ParentNoteResponse
import com.schoolconnect.app.datasource.network.response.ParentNoteReplyResponse
import com.schoolconnect.app.datasource.network.response.TeacherParentNoteStatisticsResponse
import com.schoolconnect.app.datasource.network.response.serverTotalCountOrNull
import com.schoolconnect.app.feature.common.PaginationPolicy
import com.schoolconnect.app.util.scLog

data class ParentNotesPageResult(val items: List<ParentNoteResponse>, val totalCount: Int)

object ParentNotesRepository {
    private const val TAG = "[DATA_RETRIEVE_SC] ParentNotesRepository"

    suspend fun loadPage(
        search: String? = null,
        status: String? = null,
        classSectionId: String? = null,
        studentId: String? = null,
        pageIndex: Int = 0,
        pageSize: Int = PaginationPolicy.DEFAULT_PAGE_SIZE,
        sortBy: String? = null,
        order: String? = null
    ): Result<ParentNotesPageResult> {
        val safePage = pageIndex.coerceAtLeast(0) + 1
        val safeSize = pageSize.coerceIn(1, 100)
        scLog("$TAG loadPage start page=$safePage limit=$safeSize status=${status ?: "<any>"}")
        return KtorApiClient.getTeacherParentNotes(
            search = search,
            status = status,
            classSectionId = classSectionId,
            studentId = studentId,
            page = safePage,
            limit = safeSize,
            sortBy = sortBy,
            order = order
        )
            .map { rows -> ParentNotesPageResult(rows, rows.serverTotalCountOrNull() ?: rows.size) }
            .onFailure { throwable -> scLog("$TAG loadPage failure page=$safePage", throwable) }
    }

    suspend fun loadStatistics(): Result<TeacherParentNoteStatisticsResponse> =
        KtorApiClient.getTeacherParentNoteStatistics()
            .onFailure { throwable -> scLog("$TAG loadStatistics failure", throwable) }

    suspend fun createNote(request: ParentNoteCreateRequest, currentUserId: String? = null): Result<ParentNoteResponse> {
        val normalized = request.copy(
            schoolId = request.schoolId.trim(),
            studentId = request.studentId.trim(),
            parentId = request.parentId.trim(),
            title = request.title?.trim()?.takeIf { it.isNotBlank() },
            message = request.message.trim(),
            attachmentFileIds = request.attachmentFileIds.map(String::trim).filter(String::isNotBlank)
        )
        if (normalized.schoolId.isBlank() || normalized.studentId.isBlank() || normalized.parentId.isBlank() || normalized.message.isBlank()) {
            return Result.failure(IllegalArgumentException("schoolId, studentId, parentId, and message are required"))
        }
        scLog("$TAG createNote start schoolId=${normalized.schoolId} studentId=${normalized.studentId}")
        return KtorApiClient.createParentNote(normalized, currentUserId)
            .mapCatching { createdNotes ->
                createdNotes.singleOrNull()
                    ?: throw IllegalStateException(
                        "Create parent note returned ${createdNotes.size} records; expected one for the selected student"
                    )
            }
            .onSuccess { scLog("$TAG createNote success studentId=${normalized.studentId}") }
            .onFailure { throwable -> scLog("$TAG createNote failure studentId=${normalized.studentId}", throwable) }
    }

    suspend fun loadDetail(parentNoteId: String): Result<ParentNoteResponse> =
        KtorApiClient.getParentNote(parentNoteId)
            .onFailure { throwable -> scLog("$TAG loadDetail failure parentNoteId=$parentNoteId", throwable) }

    suspend fun deleteNote(parentNoteId: String): Result<Unit> =
        KtorApiClient.deleteParentNote(parentNoteId)
            .onFailure { throwable -> scLog("$TAG deleteNote failure parentNoteId=$parentNoteId", throwable) }

    suspend fun loadReplies(parentNoteId: String): Result<List<ParentNoteReplyResponse>> =
        KtorApiClient.getParentNoteReplies(parentNoteId)
            .onFailure { throwable -> scLog("$TAG loadReplies failure parentNoteId=$parentNoteId", throwable) }

    suspend fun updateNote(parentNoteId: String, request: ParentNoteUpdateRequest, currentUserId: String? = null): Result<ParentNoteResponse> {
        val normalizedId = parentNoteId.trim()
        val normalized = request.copy(
            title = request.title?.trim()?.takeIf { it.isNotBlank() },
            message = request.message?.trim()?.takeIf { it.isNotBlank() },
            attachmentFileIds = request.attachmentFileIds.map(String::trim).filter(String::isNotBlank)
        )
        if (normalizedId.isBlank() || normalized.message.isNullOrBlank()) {
            return Result.failure(IllegalArgumentException("parentNoteId and message are required"))
        }
        scLog("$TAG updateNote start parentNoteId=$normalizedId")
        return KtorApiClient.updateParentNote(normalizedId, normalized, currentUserId)
            .onSuccess { scLog("$TAG updateNote success parentNoteId=$normalizedId") }
            .onFailure { throwable -> scLog("$TAG updateNote failure parentNoteId=$normalizedId", throwable) }
    }

    suspend fun createReply(parentNoteId: String, message: String, attachmentFileIds: List<String> = emptyList(), currentUserId: String? = null): Result<ParentNoteReplyResponse> {
        val normalizedId = parentNoteId.trim()
        val normalizedMessage = message.trim()
        if (normalizedId.isBlank() || normalizedMessage.isBlank()) {
            return Result.failure(IllegalArgumentException("parentNoteId and message are required"))
        }
        return KtorApiClient.createParentNoteReply(
            normalizedId,
            ParentNoteReplyCreateRequest(normalizedMessage, attachmentFileIds.map(String::trim).filter(String::isNotBlank)),
            currentUserId
        ).onFailure { throwable -> scLog("$TAG createReply failure parentNoteId=$normalizedId", throwable) }
    }

    suspend fun closeNote(parentNoteId: String, currentUserId: String? = null): Result<ParentNoteResponse> =
        KtorApiClient.updateParentNote(
            parentNoteId,
            ParentNoteUpdateRequest(
                status = com.schoolconnect.app.repository.systemlookup.SystemLookupOptions.serverCode("PARENT_NOTE_STATUS", "Closed")
            ),
            currentUserId
        ).onFailure { throwable -> scLog("$TAG closeNote failure parentNoteId=$parentNoteId", throwable) }
}
